import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { JsonLdService } from './json-ld.service';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import { dic } from '../dictionary';

@Injectable()
export class GeneralService {

	public state: string = '';
	// observer for notifying which page is currently displaying
	private stateSource = new BehaviorSubject({state: '', data: ''});
	currentState = this.stateSource.asObservable();

  constructor(private toastrService: ToastrService,
              private titleService: Title,
              private metaTagService: Meta,
              private jsonLdService: JsonLdService,
              @Inject(DOCUMENT) private document: Document,
              @Inject(PLATFORM_ID) private platformId: Object) { }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }


	sendNotification(message: string) {
		this.toastrService.success(message);
	}

	updateState(state: string, gameInfo: any) {
	  this.state = state;
		// notify all subscribes that page is changed
		this.stateSource.next({state: state, data: gameInfo?.fileName});

    this.updateMetaData(state, gameInfo);
	}

  updateFavouriteEvent() {
    this.stateSource.next({state: 'favMark', data: ''});
  }

	updateMetaData(state: string, gameInfo: any) {
	  const metaDataInfo = {
      title: `${dic.CONSTANTS.appTitle}`,
      description: dic.CONSTANTS.metaData.desc,
      keywords: dic.CONSTANTS.metaData.keywords,

      // Facebook
      url: `${dic.CONSTANTS.appUrL}${state}`,
      image: `${dic.CONSTANTS.appUrL}assets/images/`
    };

    const genreDescriptions: Record<string, string> = {
      [dic.STATES.action]: 'Play classic Action DOS games online! Relive retro action favorites directly in your browser—no downloads needed.',
      [dic.STATES.strategy]: 'Play classic Strategy DOS games online! Challenge your mind with retro DOS strategy masterpieces—no downloads needed.',
      [dic.STATES.arcade]: 'Play classic Arcade DOS games online! Enjoy fast-paced retro arcade fun directly in your browser—no downloads needed.',
      [dic.STATES.sport]: 'Play classic Sports DOS games online! Experience retro DOS sports games in your browser—no downloads needed.',
      [dic.STATES.adventure]: 'Play classic Adventure DOS games online! Explore retro DOS worlds and solve puzzles—no downloads needed.',
    };

	  if (gameInfo) {
	    metaDataInfo.title = `${gameInfo.name || ''}: play DOS games online for free`;
      metaDataInfo.description = gameInfo.meta || dic.CONSTANTS.metaData.desc;
      if (gameInfo.fileName) {
        metaDataInfo.url += `/${gameInfo.fileName}`;
        metaDataInfo.image += `${gameInfo.fileName}.webp`;
      }
      if (metaDataInfo.keywords) {
        metaDataInfo.keywords = `${gameInfo.name},${metaDataInfo.keywords}`;
      }
    }
	  else {
      metaDataInfo.title = `DOS Games World: play DOS games online for free`;
      metaDataInfo.description = genreDescriptions[state] ?? dic.CONSTANTS.metaData.desc;
      metaDataInfo.image += `logo.png`;
    }

    // Item #4 — noindex for non-content pages
    const noIndexStates = new Set([dic.STATES.favourites, dic.STATES.contactUs, dic.STATES.dosView, dic.STATES.policy, dic.STATES.terms]);
    this.metaTagService.updateTag({ name: 'robots', content: noIndexStates.has(state) ? 'noindex,nofollow' : 'index,follow' });

    this.titleService.setTitle(metaDataInfo.title);

    this.metaTagService.updateTag({ name: 'description', content: metaDataInfo.description });
    this.metaTagService.updateTag({ name: 'keywords', content: metaDataInfo.keywords });

    // Facebook
    this.metaTagService.updateTag({ property: 'og:type', content: gameInfo?.fileName ? 'video.game' : 'website' });
    this.metaTagService.updateTag({ property: 'og:title', content: metaDataInfo.title });
    this.metaTagService.updateTag({ property: 'og:url', content: metaDataInfo.url });
    this.metaTagService.updateTag({ property: 'og:description', content: metaDataInfo.description });
    this.metaTagService.updateTag({ property: 'og:image', content: metaDataInfo.image });
    this.metaTagService.updateTag({ property: 'og:image:alt', content: gameInfo ? gameInfo.name : dic.CONSTANTS.appTitle });
    if (gameInfo?.fileName) {
      this.metaTagService.updateTag({ property: 'og:image:type', content: 'image/webp' });
      this.metaTagService.removeTag("property='og:image:width'");
      this.metaTagService.removeTag("property='og:image:height'");
    } else {
      this.metaTagService.updateTag({ property: 'og:image:type', content: 'image/png' });
      this.metaTagService.updateTag({ property: 'og:image:width', content: '320' });
      this.metaTagService.updateTag({ property: 'og:image:height', content: '200' });
    }

    // Item #3 — og:video for YouTube
    if (gameInfo?.youtube) {
      this.metaTagService.updateTag({ property: 'og:video', content: `https://www.youtube.com/embed/${gameInfo.youtube}` });
      this.metaTagService.updateTag({ property: 'og:video:type', content: 'text/html' });
      this.metaTagService.updateTag({ property: 'og:video:width', content: '560' });
      this.metaTagService.updateTag({ property: 'og:video:height', content: '315' });
    } else {
      ['og:video', 'og:video:type', 'og:video:width', 'og:video:height'].forEach(p =>
        this.metaTagService.removeTag(`property='${p}'`));
    }

    // Twitter
    this.metaTagService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaTagService.updateTag({ name: 'twitter:title', content: metaDataInfo.title });
    this.metaTagService.updateTag({ name: 'twitter:url', content: metaDataInfo.url });
    this.metaTagService.updateTag({ name: 'twitter:description', content: metaDataInfo.description });
    this.metaTagService.updateTag({ name: 'twitter:image', content: metaDataInfo.image });
    this.metaTagService.updateTag({ name: 'twitter:image:alt', content: gameInfo ? gameInfo.name : dic.CONSTANTS.appTitle });

    // @ts-ignore
    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']");

    if (link) {
      link.href = metaDataInfo.url;
    }
    else {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', metaDataInfo.url);
      this.document.head.appendChild(link);
    }

    this.updateJsonLd(metaDataInfo, gameInfo);
  }

  updateJsonLd(metaDataInfo: any, gameInfo: any) {
    if (!gameInfo) {
      this.jsonLdService.removeStructuredData();
      this.jsonLdService.insertStructuredData({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': dic.CONSTANTS.appTitle,
        'url': dic.CONSTANTS.appUrL,
        'description': dic.CONSTANTS.metaData.desc
      });
      return;
    }

    const genreUrl = `${dic.CONSTANTS.appUrL}games/${gameInfo.genre?.toLowerCase()}`;

    const jsonLdObj = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "VideoGame",
          "name": gameInfo.name,
          "url": metaDataInfo.url,
          "image": metaDataInfo.image,
          "description": metaDataInfo.description,
          "genre": gameInfo.genre,
          "datePublished": `${gameInfo.year}-01-01`,
          ...(gameInfo.rating?.counter > 0 ? {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": Math.round(gameInfo.rating.sum / gameInfo.rating.counter),
              "reviewCount": gameInfo.rating.counter
            }
          } : {}),
          "interactionStatistic": {
            "@type": "InteractionCounter",
            "interactionType": "https://schema.org/PlayAction",
            "userInteractionCount": gameInfo.counter
          },
          "operatingSystem": "MS-DOS",
          "gamePlatform": "Web Browser",
          "applicationCategory": "Game",
          "playMode": "SinglePlayer",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
            "eligibleRegion": {
              "@type": "Place",
              "name": "Worldwide"
            }
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": dic.CONSTANTS.appUrL },
            { "@type": "ListItem", "position": 2, "name": "Games", "item": `${dic.CONSTANTS.appUrL}games` },
            { "@type": "ListItem", "position": 3, "name": gameInfo.genre, "item": genreUrl },
            { "@type": "ListItem", "position": 4, "name": gameInfo.name, "item": metaDataInfo.url }
          ]
        }
      ]
    };

    this.jsonLdService.removeStructuredData();
    this.jsonLdService.insertStructuredData(jsonLdObj);
  }

  updateCollectionSchema(genre: string, games: any[]) {
    const url = `${dic.CONSTANTS.appUrL}games/${genre.toLowerCase()}`;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': `${genre} DOS Games`,
      'url': url,
      'description': `Play free classic ${genre} DOS games online in your browser`,
      'hasPart': games.slice(0, 20).map(g => ({
        '@type': 'VideoGame',
        'name': g.name,
        'url': `${dic.CONSTANTS.appUrL}game/${g.fileName}`,
        'image': `${dic.CONSTANTS.appUrL}assets/images/${g.fileName}.webp`,
        'genre': g.genre,
      }))
    };
    this.jsonLdService.removeStructuredData();
    this.jsonLdService.insertStructuredData(schema);
  }
}
